import React from 'react';
import { Body5, Body7, Container } from '@ui';
import { Select } from '@ui/inputs';
import { SkeletonButton } from '@ui/buttons';
import { specDisplayName } from '@features/scoreSpec/format';

// Step one: which spec are we copying.
//
// Approved only, because a duplicate inherits behaviour and an approved spec is the only kind
// whose behaviour anyone has signed off on. Drafts and locked specs are still editable or still
// unproven; copying one propagates a question nobody has answered yet.
//
// A source with no output contract is offered but disabled WITH its reason, rather than filtered
// out — a spec that silently is not in the list reads as a loading bug, and the operator who went
// looking for it deserves to be told why it cannot be copied. The check only fires on rows that
// actually carry a `spec_json`; an unknown definition is not evidence of a broken one.

export const SourcePicker = ({ sources, isLoading, error, onReload, value, onSelect, canDuplicateSource }) => {
  if (isLoading) return <Body5 isLowContrast>Loading approved specs…</Body5>;

  if (error) {
    return (
      <div>
        <Body5>{error}</Body5>
        <Container top={16}>
          <SkeletonButton onClick={onReload}>Retry</SkeletonButton>
        </Container>
      </div>
    );
  }

  if (!sources.length) {
    return (
      <Body5 isLowContrast>
        No approved specs to copy. A spec has to be locked and approved before it can be the source for a duplicate.
      </Body5>
    );
  }

  const options = sources.map((spec) => {
    const usable = canDuplicateSource(spec.spec_json);
    return {
      value: spec.score_spec_id,
      label: `${specDisplayName(spec)} — ${spec.score_spec_id}`,
      disabled: !usable,
      reason: usable ? undefined : 'no output contract declared',
    };
  });

  return (
    <div>
      <Select
        id="duplicate-source-spec"
        label="Source spec"
        placeholder="Pick the spec to copy…"
        value={value}
        onChange={onSelect}
        options={options}
        hint="Approved specs only. Everything except the name, prompt and description is carried across untouched."
      />
      <Container top={8}>
        <Body7 isLowContrast>{`${sources.length} approved spec${sources.length === 1 ? '' : 's'}.`}</Body7>
      </Container>
    </div>
  );
};
